import React, { useEffect, useState } from "react";
import { api, formatApiError, fmtQty, todayStr } from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { LineItemsEditor, emptyLine, linesValid, optionalLinesValid } from "@/components/stock/LineItemsEditor";
import { FormField, WarehouseSelect } from "@/components/stock/FormField";
import { toast } from "sonner";

const blank = () => ({
  date: todayStr(), warehouse: "printing", ref_no: "", note: "",
  inputs: [emptyLine()], outputs: [emptyLine()], rejects: [],
});
const total = (lines) => lines.reduce((s, l) => s + Number(l.qty || 0), 0);
const toApi = (lines) => lines.map((l) => ({ item_type: l.item_type, item_id: l.item_id, qty: Number(l.qty) }));

const Section = ({ title, hint, tone, children }) => (
  <div>
    <Label className={`text-xs uppercase tracking-wider font-semibold ${tone}`}>{title}</Label>
    {hint && <span className="ml-2 text-xs text-slate-400">{hint}</span>}
    <div className="mt-1">{children}</div>
  </div>
);

// Printing process: e.g. 100 A1 -> 60 A2 + 35 A3, 5 reject (reject kept as separate stock).
export const ConversionDialog = ({ open, onClose, items, onSaved }) => {
  const { t } = useLang();
  const [form, setForm] = useState(blank());
  useEffect(() => { if (open) setForm(blank()); }, [open]);
  const set = (k) => (v) => setForm({ ...form, [k]: v });
  const tin = total(form.inputs);
  const tout = total(form.outputs) + total(form.rejects);

  const save = async () => {
    if (!linesValid(form.inputs) || !optionalLinesValid(form.outputs) || !optionalLinesValid(form.rejects))
      return toast.error(t("lines_invalid"));
    if (form.outputs.length + form.rejects.length === 0) return toast.error(t("conversion_need_output"));
    try {
      const { data } = await api.post("/stock-conversions", {
        ...form, inputs: toApi(form.inputs), outputs: toApi(form.outputs), rejects: toApi(form.rejects),
      });
      toast.success(`${t("saved")} · ${data.doc_no}`);
      onClose();
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="conversion-dialog">
        <DialogHeader><DialogTitle>{t("conversion")}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <FormField label={t("warehouse")}>
            <WarehouseSelect value={form.warehouse} onChange={set("warehouse")} availableOnly testid="conversion-warehouse-select" />
          </FormField>
          <FormField label={t("date")}><Input type="date" value={form.date} onChange={(e) => set("date")(e.target.value)} /></FormField>
          <FormField label={t("ref_no")}><Input value={form.ref_no} onChange={(e) => set("ref_no")(e.target.value)} data-testid="conversion-ref-input" /></FormField>
          <FormField label={t("note")}><Input value={form.note} onChange={(e) => set("note")(e.target.value)} /></FormField>
        </div>
        <Section title={t("inputs")} tone="text-red-700">
          <LineItemsEditor lines={form.inputs} onChange={set("inputs")} items={items} showStock warehouse={form.warehouse} testid="conv-inputs" />
        </Section>
        <Section title={t("outputs")} tone="text-emerald-700">
          <LineItemsEditor lines={form.outputs} onChange={set("outputs")} items={items} minLines={0} testid="conv-outputs" />
        </Section>
        <Section title={t("rejects")} hint={t("reject_note")} tone="text-rose-700">
          <LineItemsEditor lines={form.rejects} onChange={set("rejects")} items={items} minLines={0} testid="conv-rejects" />
        </Section>
        <div className="grid grid-cols-3 gap-3 text-sm bg-slate-50 border border-slate-200 rounded-md p-3" data-testid="conversion-totals">
          <div>{t("inputs")}: <span className="font-mono font-semibold">{fmtQty(tin)}</span></div>
          <div>{t("outputs")} + {t("rejects")}: <span className="font-mono font-semibold">{fmtQty(tout)}</span></div>
          <div className={tout === tin ? "text-emerald-700" : "text-amber-700"}>
            {t("difference")}: <span className="font-mono font-semibold">{fmtQty(tout - tin)}</span>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("cancel")}</Button>
          <Button onClick={save} className="bg-violet-700 hover:bg-violet-800" data-testid="conversion-save-btn">{t("save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
